<template>
    <div class="slider-box" v-if="items.length" :style="{ 'background-color': color }">
        <Slider
            :colPreloadCount="2"
            :items="items"
            :itemsInCol="1"
            :loop="false"
            :autoScroll="true"
            :storyItem="true"
            itemWidth="100%"
            itemHeight="58px"
            colAlign="center"
            colGap="8px"
            rowGap="8px"
            ref="sliderRef"
            @change="onChange"
        >
            <template #default="{ item }">
                <div class="slide">
                    <span v-if="id === item.id"
                        class="progress"
                        :style="{ width: pos }"
                    />

                    <TrackPreview
                        :item="item"
                        :playable="false"
                        :light="true"
                        :showKebab="false"
                        :checkcolor="true"
                        :viewonly="true"
                        size="size-m"
                        @color="data.colors[item.id] = $event"
                    />

                    <Reaction
                        entity="tracks"
                        :id="item.id"
                        size="size-l"
                    />

                    <AudioToggle
                        :state="id === item.id ? player.panes.default?._state : null"
                        :light="true"
                        class="size-m secondary dirty"
                        :touchMode="true"
                        :class="{ invert: others.theme !== 'dark' }"
                        @play="play"
                        @pause="pause"
                    />
                </div>
            </template>
        </Slider>
    </div>
</template>

<script setup>
    import { reactive, ref, computed, watch } from 'vue';
    import stores from '@/stores';
    import Slider from '🔗/components/ui/Slider.vue';
    import TrackPreview from '🔗/components/ui/TrackPreview.vue';
    import IconButton from '🔗/components/ui/IconButton.vue';
    import { message } from '🔗/utils/utils';
    import AudioToggle from '🔗/components/ui/AudioToggle.vue';
    import { useI18n } from 'vue-i18n';
    import Reaction from '🔗/components/ui/Reaction.vue';

    const { t } = useI18n();
    const sliderRef = ref(null);
    const player = stores.player();
    const others = stores.others();

    const data = reactive({
        colors: {}
    });

    const color = computed(() => {
        if (!id?.value || !data.colors[id.value]) {
            return;
        }

        const color = data.colors[id.value];
        return `rgba(${color.r},${color.g},${color.b}, .9)`;
    });

    const id = computed(() => player.panes.default?._item?.id);

    const pos = computed(() => {
        const pane = player.panes.default;

        if (!pane || !pane._item?.id || !pane._item.duration) {
            return 0;
        }

        const pos = Math.max(0, Math.min((pane._playedSeconds * 1000), pane._item.duration));

        return Math.floor(100 * pos / pane._item.duration) + '%';
    });

    watch(
        () => id.value,
        (value) => {
            setTimeout(() => {
                if (value && sliderRef.value?.showItem) {
                    sliderRef.value.showItem(value);
                }
            });
        }
    );

    const items = computed(() => {
        if (!player.panes.default) {
            return [];
        }

        const queue = player.panes.default.queue();

        if (!queue?.items?.length) {
            if (player.panes.default._item?.id) {
                return [player.panes.default._item];
            }

            return [];
        }

        return queue?.items;
    });

    const onChange = (payload) => {
        if (!payload?.length) {
            return;
        }

        if (!player.panes.default) {
            return [];
        }

        if (!payload[0].id || payload[0].id === id.value) {
            return;
        }

        player.panes.default.playById(payload[0].id);
    }

    const play = async () => {
        await player.play();
    }

    const pause = async () => {
        await player.pause();
    }
</script>

<style lang="scss" scoped>
    .slider-box {
        background: var(--white-80);
        height: var(--player-preview-height);
        font: var(--font-h5);
        display: flex;
        align-items: center;
        justify-content: center;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        // border-bottom-left-radius: 8px;
        // border-bottom-right-radius: 8px;
        width: 100%;
        overflow: hidden;
        transition: background-color 0.8s ease;

        .slider {
            padding: 0px 16px;
            scroll-padding-left: 16px;
            scroll-padding-right: 16px;
        }
    }

    .slide {
        position: relative;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        width: 100%;
        height: 100%;
        background: var(--light-80);
        gap: 0px;
        padding: 8px;

        & > .progress {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            border-radius: 10px;
            background: var(--white-80);
        }

        .track-preview {
            z-index: 1;
            pointer-events: none;
        }

        & > button {
            flex-shrink: 0;
            z-index: 1;
        }

    }

    .direction-rtl {
        .slide {
            & > .progress {
                right: 0;
                left: auto;
            }
        }
    }
</style>
